import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ValentinesPuzzle.css';

interface PuzzlePiece {
  id: number;
  correctSlot: number;
  placedInSlot: number | null;
  row: number;
  col: number;
  tabs: { top: boolean; right: boolean; bottom: boolean; left: boolean };
}

type Difficulty = '5x5' | '7x7' | '10x10' | '20x20';

interface ValentinesPuzzleProps {
  imageUrls: string[];
  difficulty?: Difficulty;
}

const ValentinesPuzzle: React.FC<ValentinesPuzzleProps> = ({ 
  imageUrls,
  difficulty = '5x5' 
}) => {
  const frameCanvasRef = useRef<HTMLCanvasElement>(null);
  const trayCanvasRef = useRef<HTMLCanvasElement>(null);
  const dragCanvasRef = useRef<HTMLCanvasElement>(null);
  const [pieces, setPieces] = useState<PuzzlePiece[]>([]);
  const [draggedPieceId, setDraggedPieceId] = useState<number | null>(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [gridSize, setGridSize] = useState({ rows: 5, cols: 5 });
  const [pieceSize, setPieceSize] = useState(100);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDifficulty, setCurrentDifficulty] = useState<Difficulty>(difficulty);

  useEffect(() => {
    const sizes = {
      '5x5': { rows: 5, cols: 5 },
      '7x7': { rows: 7, cols: 7 },
      '10x10': { rows: 10, cols: 10 },
      '20x20': { rows: 20, cols: 20 }
    };
    setGridSize(sizes[currentDifficulty]);
  }, [currentDifficulty]);

  useEffect(() => {
    loadRandomImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrls]);

  const loadRandomImage = () => {
    if (imageUrls.length === 0) return;
    const randomIndex = Math.floor(Math.random() * imageUrls.length);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      setImage(img);
      initializeCanvas(img);
    };
    img.src = imageUrls[randomIndex];
  };

  const initializeCanvas = (img: HTMLImageElement) => {
    const maxSize = 600;
    const aspectRatio = img.width / img.height;
    
    let width: number;
    let height: number;
    
    if (aspectRatio > 1) {
      width = Math.min(maxSize, window.innerWidth - 80);
      height = width / aspectRatio;
    } else {
      height = Math.min(maxSize, window.innerHeight * 0.5);
      width = height * aspectRatio;
    }
    
    // Ensure width and height are exact multiples of grid size
    const pSize = Math.min(width / gridSize.cols, height / gridSize.rows);
    width = pSize * gridSize.cols;
    height = pSize * gridSize.rows;
    
    setCanvasSize({ width, height });
    setPieceSize(pSize);

    setTimeout(() => initializePuzzle(width, height, pSize), 100);
  };

  const drawPuzzlePiece = useCallback((
  ctx: CanvasRenderingContext2D,
  piece: PuzzlePiece,
  size: number,
  drawMode: 'clip' | 'stroke'
  ) => {
    const tabHeight = size * 0.15;
    
    ctx.beginPath();
    
    // Top edge
    ctx.moveTo(0, 0);
    if (piece.row === 0) {
      ctx.lineTo(size, 0);
    } else {
      ctx.lineTo(size * 0.35, 0);
      if (piece.tabs.top) {
        ctx.bezierCurveTo(size * 0.35, -tabHeight, size * 0.65, -tabHeight, size * 0.65, 0);
      } else {
        ctx.bezierCurveTo(size * 0.35, tabHeight, size * 0.65, tabHeight, size * 0.65, 0);
      }
      ctx.lineTo(size, 0);
    }
    
    // Right edge
    if (piece.col === gridSize.cols - 1) {
      ctx.lineTo(size, size);
    } else {
      ctx.lineTo(size, size * 0.35);
      if (piece.tabs.right) {
        ctx.bezierCurveTo(size + tabHeight, size * 0.35, size + tabHeight, size * 0.65, size, size * 0.65);
      } else {
        ctx.bezierCurveTo(size - tabHeight, size * 0.35, size - tabHeight, size * 0.65, size, size * 0.65);
      }
      ctx.lineTo(size, size);
    }
    
    // Bottom edge
    if (piece.row === gridSize.rows - 1) {
      ctx.lineTo(0, size);
    } else {
      ctx.lineTo(size * 0.65, size);
      if (piece.tabs.bottom) {
        ctx.bezierCurveTo(size * 0.65, size + tabHeight, size * 0.35, size + tabHeight, size * 0.35, size);
      } else {
        ctx.bezierCurveTo(size * 0.65, size - tabHeight, size * 0.35, size - tabHeight, size * 0.35, size);
      }
      ctx.lineTo(0, size);
    }
    
    // Left edge
    if (piece.col === 0) {
      ctx.lineTo(0, 0);
    } else {
      ctx.lineTo(0, size * 0.65);
      if (piece.tabs.left) {
        ctx.bezierCurveTo(-tabHeight, size * 0.65, -tabHeight, size * 0.35, 0, size * 0.35);
      } else {
        ctx.bezierCurveTo(tabHeight, size * 0.65, tabHeight, size * 0.35, 0, size * 0.35);
      }
      ctx.lineTo(0, 0);
    }
    
    ctx.closePath();
    
    if (drawMode === 'clip') {
      ctx.clip();
    } else {
      ctx.stroke();
    }
  }, [gridSize]);

  const initializePuzzle = (width: number, height: number, size: number) => {
    const totalPieces = gridSize.rows * gridSize.cols;
    const newPieces: PuzzlePiece[] = [];
    
    const tabPattern: boolean[][] = [];
    for (let row = 0; row < gridSize.rows; row++) {
      tabPattern[row] = [];
      for (let col = 0; col < gridSize.cols; col++) {
        tabPattern[row][col] = Math.random() > 0.5;
      }
    }

    for (let i = 0; i < totalPieces; i++) {
      const row = Math.floor(i / gridSize.cols);
      const col = i % gridSize.cols;

      newPieces.push({
        id: i,
        correctSlot: i,
        placedInSlot: null,
        row,
        col,
        tabs: {
          top: row > 0 ? !tabPattern[row - 1][col] : false,
          right: col < gridSize.cols - 1 ? tabPattern[row][col] : false,
          bottom: row < gridSize.rows - 1 ? tabPattern[row][col] : false,
          left: col > 0 ? !tabPattern[row][col - 1] : false
        }
      });
    }

    newPieces.sort(() => Math.random() - 0.5);
    
    setPieces(newPieces);
    setIsComplete(false);
    setDraggedPieceId(null);
  };

  useEffect(() => {
    if (!frameCanvasRef.current || !image || pieces.length === 0) return;
    
    const canvas = frameCanvasRef.current;
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate source image piece size
    const srcPieceWidth = image.width / gridSize.cols;
    const srcPieceHeight = image.height / gridSize.rows;

    for (let i = 0; i < gridSize.rows * gridSize.cols; i++) {
      const piece = pieces.find(p => p.placedInSlot === i);
      
      if (piece) {
        const slotRow = Math.floor(i / gridSize.cols);
        const slotCol = i % gridSize.cols;
        const x = slotCol * pieceSize;
        const y = slotRow * pieceSize;

        // Source coordinates from original image
        const srcX = piece.col * srcPieceWidth;
        const srcY = piece.row * srcPieceHeight;

        ctx.save();
        ctx.translate(x, y);
        drawPuzzlePiece(ctx, piece, pieceSize, 'clip');
        
        // Draw from original image with proper source coordinates
        ctx.drawImage(
          image,
          srcX, srcY, srcPieceWidth, srcPieceHeight,
          0, 0, pieceSize, pieceSize
        );

        ctx.restore();

        ctx.save();
        ctx.translate(x, y);
        ctx.strokeStyle = piece.correctSlot === i ? '#28a745' : '#999';
        ctx.lineWidth = piece.correctSlot === i ? 2.5 : 1;
        drawPuzzlePiece(ctx, piece, pieceSize, 'stroke');
        ctx.restore();
      }
    }
  }, [pieces, image, canvasSize, pieceSize, gridSize, drawPuzzlePiece]);

  useEffect(() => {
    if (!trayCanvasRef.current || !image || pieces.length === 0) return;
    
    const canvas = trayCanvasRef.current;
    const piecesInTray = pieces.filter(p => p.placedInSlot === null && p.id !== draggedPieceId);
    
    const piecesPerRow = Math.floor(Math.min(800, window.innerWidth - 40) / (pieceSize * 1.3));
    const rows = Math.ceil(piecesInTray.length / piecesPerRow);
    
    canvas.width = Math.min(800, window.innerWidth - 40);
    canvas.height = Math.max(100, rows * (pieceSize * 1.3) + 20);
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate source image piece size
    const srcPieceWidth = image.width / gridSize.cols;
    const srcPieceHeight = image.height / gridSize.rows;

    piecesInTray.forEach((piece, index) => {
      const trayCol = index % piecesPerRow;
      const trayRow = Math.floor(index / piecesPerRow);
      
      const x = trayCol * (pieceSize * 1.3) + pieceSize * 0.15 + 10;
      const y = trayRow * (pieceSize * 1.3) + pieceSize * 0.15 + 10;

      // Source coordinates from original image
      const srcX = piece.col * srcPieceWidth;
      const srcY = piece.row * srcPieceHeight;

      ctx.save();
      ctx.translate(x, y);
      drawPuzzlePiece(ctx, piece, pieceSize, 'clip');
      
      // Draw from original image with proper source coordinates
      ctx.drawImage(
        image,
        srcX, srcY, srcPieceWidth, srcPieceHeight,
        0, 0, pieceSize, pieceSize
      );

      ctx.restore();

      ctx.save();
      ctx.translate(x, y);
      ctx.strokeStyle = '#666';
      ctx.lineWidth = 2;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 5;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      drawPuzzlePiece(ctx, piece, pieceSize, 'stroke');
      ctx.restore();
    });
  }, [pieces, image, pieceSize, gridSize, draggedPieceId, drawPuzzlePiece]);

  useEffect(() => {
    if (draggedPieceId === null || !dragCanvasRef.current || !image) return;
    
    const piece = pieces.find(p => p.id === draggedPieceId);
    if (!piece) return;
    
    const canvas = dragCanvasRef.current;
    canvas.width = pieceSize * 1.5;
    canvas.height = pieceSize * 1.5;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate source image piece size
    const srcPieceWidth = image.width / gridSize.cols;
    const srcPieceHeight = image.height / gridSize.rows;
    
    // Source coordinates from original image
    const srcX = piece.col * srcPieceWidth;
    const srcY = piece.row * srcPieceHeight;
    
    ctx.save();
    ctx.translate(pieceSize * 0.25, pieceSize * 0.25);
    drawPuzzlePiece(ctx, piece, pieceSize, 'clip');
    
    // Draw from original image with proper source coordinates
    ctx.drawImage(
      image,
      srcX, srcY, srcPieceWidth, srcPieceHeight,
      0, 0, pieceSize, pieceSize
    );

    ctx.restore();

    ctx.save();
    ctx.translate(pieceSize * 0.25, pieceSize * 0.25);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 3;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 3;
    ctx.shadowOffsetY = 3;
    drawPuzzlePiece(ctx, piece, pieceSize, 'stroke');
    ctx.restore();
  }, [draggedPieceId, dragPosition, pieces, image, pieceSize, gridSize, drawPuzzlePiece]);

  useEffect(() => {
    if (pieces.length === 0) return;
    
    const allPlaced = pieces.every(p => p.placedInSlot !== null);
    const allCorrect = pieces.every(p => p.placedInSlot === p.correctSlot);
    
    if (allPlaced && allCorrect) {
      setTimeout(() => setIsComplete(true), 500);
    }
  }, [pieces]);

  const handleTrayMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = trayCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const piecesInTray = pieces.filter(p => p.placedInSlot === null);
    const piecesPerRow = Math.floor(canvas.width / (pieceSize * 1.3));

    for (let i = piecesInTray.length - 1; i >= 0; i--) {
      const piece = piecesInTray[i];
      const trayCol = i % piecesPerRow;
      const trayRow = Math.floor(i / piecesPerRow);
      
      const pieceX = trayCol * (pieceSize * 1.3) + pieceSize * 0.15 + 10;
      const pieceY = trayRow * (pieceSize * 1.3) + pieceSize * 0.15 + 10;

      if (x >= pieceX && x <= pieceX + pieceSize && y >= pieceY && y <= pieceY + pieceSize) {
        setDraggedPieceId(piece.id);
        setDragPosition({ x: e.clientX, y: e.clientY });
        return;
      }
    }
  };

  const handleFrameMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = frameCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    for (let i = 0; i < pieces.length; i++) {
      const piece = pieces[i];
      if (piece.placedInSlot === null) continue;

      const slotRow = Math.floor(piece.placedInSlot / gridSize.cols);
      const slotCol = piece.placedInSlot % gridSize.cols;
      const pieceX = slotCol * pieceSize;
      const pieceY = slotRow * pieceSize;

      if (x >= pieceX && x <= pieceX + pieceSize && y >= pieceY && y <= pieceY + pieceSize) {
        setDraggedPieceId(piece.id);
        setDragPosition({ x: e.clientX, y: e.clientY });
        
        setPieces(pieces.map(p => 
          p.id === piece.id ? { ...p, placedInSlot: null } : p
        ));
        
        return;
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (draggedPieceId !== null) {
      setDragPosition({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (draggedPieceId === null) return;

    const canvas = frameCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (x >= 0 && x <= canvasSize.width && y >= 0 && y <= canvasSize.height) {
      const col = Math.floor(x / pieceSize);
      const row = Math.floor(y / pieceSize);
      const slotIndex = row * gridSize.cols + col;

      if (slotIndex >= 0 && slotIndex < gridSize.rows * gridSize.cols) {
        const slotOccupied = pieces.find(p => p.placedInSlot === slotIndex);
        
        if (!slotOccupied) {
          setPieces(pieces.map(p => 
            p.id === draggedPieceId ? { ...p, placedInSlot: slotIndex } : p
          ));
        }
      }
    }

    setDraggedPieceId(null);
  };

  const changeDifficulty = (newDifficulty: Difficulty) => {
    setCurrentDifficulty(newDifficulty);
    if (image) {
      setTimeout(() => {
        const sizes = {
          '5x5': { rows: 5, cols: 5 },
          '7x7': { rows: 7, cols: 7 },
          '10x10': { rows: 10, cols: 10 },
          '20x20': { rows: 20, cols: 20 }
        };
        setGridSize(sizes[newDifficulty]);
        initializeCanvas(image);
      }, 100);
    }
  };

  return (
    <div 
      className="valentine-puzzle-container" 
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <div className="puzzle-header">
        <h1 className="puzzle-title">💝 Valentine's Puzzle 💝</h1>
        <p className="puzzle-subtitle">A special gift made with love</p>
      </div>

      <div className="difficulty-selector">
        {(['5x5', '7x7', '10x10', '20x20'] as Difficulty[]).map(level => (
          <button
            key={level}
            className={`difficulty-btn ${currentDifficulty === level ? 'active' : ''}`}
            onClick={() => changeDifficulty(level)}
          >
            {level}
          </button>
        ))}
      </div>

      <div className="puzzle-frame-section">
        <div className="frame-label">🖼️ Build Your Picture Here</div>
        <canvas
          ref={frameCanvasRef}
          className="puzzle-frame"
          onMouseDown={handleFrameMouseDown}
        />
        <div className="progress-indicator">
          {pieces.filter(p => p.placedInSlot !== null).length} / {pieces.length} pieces placed
        </div>
      </div>

      <div className="puzzle-tray-section">
        <div className="tray-label">🧩 Drag Pieces From Here</div>
        <canvas
          ref={trayCanvasRef}
          className="puzzle-tray"
          onMouseDown={handleTrayMouseDown}
        />
      </div>

      <button className="puzzle-reset-btn" onClick={loadRandomImage}>
        🎲 New Puzzle
      </button>

      {draggedPieceId !== null && (
        <canvas
          ref={dragCanvasRef}
          className="drag-canvas"
          style={{
            left: dragPosition.x - pieceSize * 0.75,
            top: dragPosition.y - pieceSize * 0.75
          }}
        />
      )}

      {isComplete && (
        <div className="completion-overlay">
          <div className="completion-message">🎉 You Did It! 🎉</div>
          <div className="hearts">💕 💖 💗 💓 💝</div>
          <button className="close-btn" onClick={() => setIsComplete(false)}>
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default ValentinesPuzzle;